import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Input, Modal, useToast, v } from '@kss/ui';
import {
  useAuth,
  decodeJwt,
  extractPermissions,
  isPanelRole,
} from '@kss/auth';
import { authService } from '@kss/api';
import { PasswordField } from './PasswordField';
import { getTenantFromHost, shouldShowCompanyNameInput } from '../utils/appSettings';

export interface LoginModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * When provided, navigate here after sign-in instead of the role-based default.
   * Useful when the user clicked "Open Panel" — we always send them to the panel.
   */
  forceRedirect?: string;
  /**
   * Switch to the sign-up modal. The host listens for the
   * `kss:open-register` event after closing this modal.
   */
  onSwitchToRegister?: () => void;
  /**
   * Switch to the forgot-password modal. Mirrors the register switch
   * — the modal closes itself, then the host opens
   * `<ForgotPasswordModal>`.
   */
  onSwitchToForgotPassword?: (email?: string) => void;
}

/**
 * Inline sign-in modal for the public web. Submits the multi-tenant login
 * payload and routes by role:
 *
 * - admin / manager / staff / assigned → /panel/dashboard
 * - everyone else → stay on the public web (modal closes)
 */
export function LoginModal({
  open,
  onClose,
  forceRedirect,
  onSwitchToRegister,
  onSwitchToForgotPassword,
}: LoginModalProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { login, endedReason, lastSessionExpiresAt } = useAuth();

  /* All credential inputs default to BLANK per user request 2026-05-14 —
     the previous seeded "sso" + "admin" values were a dev convenience
     that leaked into production and risked accidental probe-cred
     logins. Hints stay in `placeholder=` so the form still tells the
     user what shape each field expects. */
  /* Default the tenant code to the current hostname's first label
     (`sso.kristysoftware.com` → `sso`). Users on a tenant-owned
     subdomain therefore never need to type their company name. When
     `VITE_SHOW_COMPANY_NAME=Y` the textbox is rendered so the user
     can override the derived value (white-label resellers etc.). */
  const [companyName, setCompanyName] = useState(() => getTenantFromHost());
  const showCompanyInput = shouldShowCompanyNameInput();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  /* Presence only. A sign-in form must accept whatever the account's
     password already is — applying the strength policy here would lock
     out every user created before it. */
  const usernameError = v.required('Username')(username);
  const passwordError = v.required('Password')(password);
  const show = (k: string, e: string | null) => (touched[k] ? e ?? undefined : undefined);
  const mark = (k: string) => () => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tracks whether the most recent error mentions email
  // verification; flips on the "Resend verification" affordance.
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);

  function reset() {
    setError(null);
    setPassword('');
    setSubmitting(false);
    setNeedsVerification(false);
  }

  /**
   * Send a fresh verification email to the address typed into the
   * Username field (most customers sign in with their email; if they
   * use a userName proper, this fails closed with a clear toast).
   * Used when the login error mentions "email not verified".
   */
  async function handleResendVerification() {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username.trim())) {
      toast.error(
        'Email needed',
        'Enter your email in the Username field, then try Resend.',
      );
      return;
    }
    setResending(true);
    try {
      await authService.sendVerification(username.trim(), companyName.trim() || undefined);
      toast.success(
        'Verification email sent',
        `Check ${username.trim()} for the new link.`,
      );
      setNeedsVerification(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not send verification email.';
      toast.error('Resend failed', msg);
    } finally {
      setResending(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setTouched({ username: true, password: true });
    if (usernameError || passwordError) {
      return;
    }
    if (showCompanyInput && !companyName.trim()) {
      setError('Company name is required.');
      return;
    }
    setSubmitting(true);
    try {
      const resp = await authService.login({
        tenantCode: companyName.trim(),
        userName: username.trim(),
        password,
      });
      // Live API returns a flat token bundle — name / email / role /
      // permissions live in the JWT claims, not in the response body.
      const claims = decodeJwt(resp.accessToken);
      const role = String(
        claims?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ??
          claims?.role ??
          ''
      );
      const displayName = String(claims?.name ?? resp.userName);
      const email = String(claims?.email ?? '');
      const permissions = extractPermissions(claims);
      login(
        {
          id: resp.userId,
          name: displayName,
          email,
          role,
          permissions,
          tenantId: resp.tenantId,
        },
        {
          accessToken: resp.accessToken,
          refreshToken: resp.refreshToken,
          expiresAt: resp.expiresAt,
          tenantId: resp.tenantId,
          sessionId: resp.sessionId,
        }
      );
      toast.success('Signed in', `Welcome, ${displayName}`);
      reset();
      onClose();
      /* Panel-role users land on the panel dashboard; everyone else
         (default "customer" role) lands on the customer dashboard at
         `/account`, NOT the storefront home. Bug ref: AI Agent
         2026-05-14 — "dashboard not found after login". */
      const target =
        forceRedirect ??
        (isPanelRole(role) ? '/panel/dashboard' : '/account');
      navigate(target);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message ??
        (err as Error)?.message ??
        'Sign-in failed';
      setError(msg);
      // Heuristic — a wide net since the backend's wording isn't
      // pinned. Hits "email not verified", "please verify your email",
      // "account requires verification", etc.
      setNeedsVerification(/verif/i.test(msg));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="Sign in to KSS"
      width={440}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          {onSwitchToRegister ? (
            <button
              type="button"
              onClick={() => { reset(); onClose(); onSwitchToRegister(); }}
              disabled={submitting}
              className="text-xs text-content-secondary hover:text-content-primary disabled:opacity-50"
            >
              Don’t have an account? <span className="font-semibold text-primary">Sign up</span>
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { reset(); onClose(); }} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !!usernameError || !!passwordError}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </div>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="grid gap-3">
        {error && <Alert variant="danger">{error}</Alert>}
        {needsVerification && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-warning bg-warning-light px-3 py-2 text-xs">
            <span className="text-warning">
              Need a fresh verification link?
            </span>
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={resending}
              className="font-semibold text-warning hover:underline disabled:opacity-60"
            >
              {resending ? 'Sending…' : 'Resend email'}
            </button>
          </div>
        )}
        {/* When the user opens the login modal after a stale session was
            auto-cleared by useSessionBootstrap, surface WHEN the session
            ended so the disconnect doesn't feel mysterious. */}
        {!error && endedReason === 'expired' && (
          <Alert variant="warning">
            Your previous session expired
            {lastSessionExpiresAt
              ? ` on ${new Date(lastSessionExpiresAt).toLocaleString()}`
              : ''}
            . Please sign in again.
          </Alert>
        )}
        {!error && endedReason === 'refresh-failed' && (
          <Alert variant="warning">
            We couldn't refresh your previous session. Please sign in again.
          </Alert>
        )}
        {/* Company-name input is hidden by default — the tenant code
            is auto-derived from the hostname (subdomain). Set
            `VITE_SHOW_COMPANY_NAME=Y` to expose this textbox for
            white-label / reseller deployments where users override
            the tenant manually. */}
        {showCompanyInput && (
          <Input
            label="Company name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Enter your tenant code (e.g. sso)"
            autoComplete="organization"
            hint="Your company's short tenant code — usually the subdomain."
          />
        )}
        <Input
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onBlur={mark('username')}
          error={show('username', usernameError)}
          placeholder="Enter your username"
          autoComplete="username"
        />
        <PasswordField
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onBlur={mark('password')}
          error={show('password', passwordError)}
          placeholder="Enter your password"
          autoComplete="current-password"
        />
        {onSwitchToForgotPassword && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                reset();
                onClose();
                // Carry the typed username over only when it looks
                // like an email — saves the user re-typing on the
                // reset modal.
                onSwitchToForgotPassword(
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username) ? username : undefined,
                );
              }}
              disabled={submitting}
              className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
            >
              Forgot password?
            </button>
          </div>
        )}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
